import { useCallback, useEffect, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

import Badge from '@/components/Badge';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

import AddWorkerBadgeModal from './AddWorkerBadgeModal';

type Props = {
  workerId: number;
};

interface WorkerHeldCertificate {
  id: number;
  name: string;
  display_name_en: string | null;
  certificate_type: 'BADGE' | 'CERTIFICATE';
  image_url: string | null;
}

const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return fallback;
};

/**
 * Badges and certificates this worker holds, with grant/revoke.
 *
 * This is the surface that went missing in the WorkerV2 migration: the only
 * grant UI lived on the legacy `Agency/Worker` page, which `/workers/:id` now
 * redirects away from, so a client badge requirement had become impossible to
 * satisfy. The older `CertificatesCard` in this folder is display-only and was
 * never mounted.
 */
const WorkerBadgesCard = ({ workerId }: Props) => {
  const [certificates, setCertificates] = useState<WorkerHeldCertificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axiosRubyClient.get(
        `/api/admin/workers/${workerId}/certificates`,
      );
      setCertificates(response.data.data || []);
    } catch (err) {
      setError(errorMessageFrom(err, 'Failed to load badges.'));
    } finally {
      setIsLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    load();
  }, [load]);

  // The API returns 409 when the worker is mid-shift on a job requiring the
  // badge — surface that reason rather than a generic failure.
  const handleRevoke = async (certificateId: number) => {
    try {
      setRevokingId(certificateId);
      setError(null);
      await axiosRubyClient.delete(
        `/api/admin/workers/${workerId}/certificates/${certificateId}`,
      );
      await load();
    } catch (err) {
      setError(errorMessageFrom(err, 'Failed to revoke badge.'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Card
      title={`Badges & Certificates (${certificates.length})`}
      actionsDropdown={
        <Button
          size="small"
          startIcon={<Add />}
          variant="outlined"
          onClick={() => setShowAddModal(true)}
        >
          Assign
        </Button>
      }
    >
      {error && (
        <Card.Section>
          <Small color="danger">{error}</Small>
        </Card.Section>
      )}

      {isLoading ? (
        <Card.Section>
          <Small color="lighter">Loading…</Small>
        </Card.Section>
      ) : certificates.length === 0 ? (
        <Card.Section>
          <Stack vertical gap={12} align="start">
            <Small color="lighter">
              This worker holds no badges. They cannot be matched to shifts that
              require one.
            </Small>
            <Button
              size="small"
              startIcon={<Add />}
              variant="outlined"
              onClick={() => setShowAddModal(true)}
            >
              Assign a badge
            </Button>
          </Stack>
        </Card.Section>
      ) : (
        certificates.map((cert) => (
          <Card.Section key={cert.id}>
            <Stack gap={12} align="center">
              {cert.image_url && (
                <img
                  alt=""
                  src={cert.image_url}
                  style={{ width: 32, height: 32, objectFit: 'contain' }}
                />
              )}
              <Stack vertical gap={2} style={{ flex: 1 }}>
                <Body weight="semibold">
                  {cert.display_name_en || cert.name}
                </Body>
              </Stack>
              <Badge
                label={cert.certificate_type === 'BADGE' ? 'Badge' : 'Cert'}
                status={cert.certificate_type === 'BADGE' ? 'theme' : 'neutral'}
              />
              <Tooltip title="Revoke">
                <span>
                  {/* An icon-only IconButton has no accessible name — a MUI
                      Tooltip supplies a description, not a label — so screen
                      readers (and role-based queries) see an unnamed button.
                      Name it after the badge so several rows stay distinct. */}
                  <IconButton
                    aria-label={`Revoke ${cert.display_name_en || cert.name}`}
                    disabled={revokingId === cert.id}
                    size="small"
                    onClick={() => handleRevoke(cert.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Card.Section>
        ))
      )}

      <AddWorkerBadgeModal
        existingCertificateIds={certificates.map((cert) => cert.id)}
        open={showAddModal}
        workerId={workerId}
        onBadgeAdded={load}
        onClose={() => setShowAddModal(false)}
      />
    </Card>
  );
};

export default WorkerBadgesCard;
